import { SEARCH_DEBOUNCE_MS } from '@/services/photovogue/defaults';
import { create } from 'zustand';

import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { createInitialState, InitialState } from '@repo/ui/state';
import {
  buildPhotoBatchPatches,
  createDefaultPhotoFilters,
  getPhotoVogueService,
  MODERATION_ACTION_TO_STATE,
} from '@/services/photovogue';
import type {
  BulkPhotoAction,
  ModerationAction,
  Pagination,
  Photo,
  PhotoFilters,
  PhotoPatch,
  PhotoQueryParams,
  PhotoSort,
} from '@/services/photovogue';

const DEFAULT_PAGINATION: Pagination = { page: 0, limit: 100, skip: 0 };
const DEFAULT_SORT: PhotoSort = { field: 'createdAt', direction: 'desc' };

const defaultParams = (): PhotoQueryParams => ({
  filters: createDefaultPhotoFilters(),
  pagination: { ...DEFAULT_PAGINATION },
  sort: { ...DEFAULT_SORT },
});

const withSkip = (pagination: Pagination): Pagination => ({
  ...pagination,
  skip: pagination.page * pagination.limit,
});

export interface PhotosStore extends InitialState<Photo[]> {
  _ids: string[];
  count: number;
  params: PhotoQueryParams;
  loadingSpinner: boolean;

  fetchPhotos: (
    params: PhotoQueryParams,
    silent?: boolean,
    debounceSearch?: boolean,
  ) => Promise<void>;
  init: () => Promise<void>;
  updateFilters: (
    filters: PhotoFilters | ((filters: PhotoFilters) => PhotoFilters),
    debounceSearch?: boolean,
  ) => Promise<void>;
  updatePagination: (
    pagination: Pagination | ((pagination: Pagination) => Pagination),
  ) => Promise<void>;
  updateSort: (
    sort: PhotoSort | ((sort: PhotoSort) => PhotoSort),
  ) => Promise<void>;
  resetFilters: () => Promise<void>;
  hasActiveFilters: () => boolean;
  moderatePhoto: (id: string, action: ModerationAction) => Promise<void>;
  moderatePhotos: (ids: string[], action: BulkPhotoAction) => Promise<void>;
  updatePhoto: (id: string, patch: PhotoPatch) => Promise<Photo | null>;
  setPhoto: (photo: Photo) => void;
  reset: () => void;
}

export const initialState = {
  _ids: [],
  count: 0,
  loadingSpinner: true,
  params: defaultParams(),
  ...createInitialState<Photo[]>([]),
};

// Only the latest list request may publish results, errors, or loading state.
let requestVersion = 0;

export const store = create<PhotosStore>((set, store) => ({
  ...initialState,

  fetchPhotos: async (params, silent = false, debounceSearch = false) => {
    const version = ++requestVersion;
    set((state) => ({
      ...state,
      loading: !silent,
      loadingSpinner: true,
      data: silent ? state.data : [],
    }));
    try {
      if (debounceSearch) {
        await new Promise((resolve) => setTimeout(resolve, SEARCH_DEBOUNCE_MS));
        if (version !== requestVersion) return;
      }
      const response = await getPhotoVogueService().getPhotos(params);
      if (version !== requestVersion) return;
      set((state) => ({
        ...state,
        error: null,
        _ids: response._ids,
        data: response.data,
        count: response.count,
      }));
    } catch (error) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to load photos' });
      set((state) => ({
        ...state,
        error: error instanceof Error ? error.message : 'Unable to load photos',
      }));
    } finally {
      if (version === requestVersion)
        set((state) => ({ ...state, loading: false, loadingSpinner: false }));
    }
  },

  init: async () => {
    await store().fetchPhotos(store().params);
  },

  updateFilters: async (filtersUpdater, debounceSearch = false) => {
    const current = store().params.filters;
    const filters =
      typeof filtersUpdater === 'function'
        ? filtersUpdater(current)
        : filtersUpdater;
    // Reset to the first page whenever filters change.
    const params: PhotoQueryParams = {
      ...store().params,
      filters,
      pagination: withSkip({ ...store().params.pagination, page: 0 }),
    };
    set((state) => ({ ...state, params }));
    await store().fetchPhotos(params, true, debounceSearch);
  },

  updatePagination: async (paginationUpdater) => {
    const current = store().params.pagination;
    const next =
      typeof paginationUpdater === 'function'
        ? paginationUpdater(current)
        : paginationUpdater;
    const params: PhotoQueryParams = {
      ...store().params,
      pagination: withSkip(next),
    };
    set((state) => ({ ...state, params }));
    await store().fetchPhotos(params);
  },

  updateSort: async (sortUpdater) => {
    const current = store().params.sort;
    const sort =
      typeof sortUpdater === 'function' ? sortUpdater(current) : sortUpdater;
    const params: PhotoQueryParams = { ...store().params, sort };
    set((state) => ({ ...state, params }));
    await store().fetchPhotos(params, true);
  },

  resetFilters: async () => {
    await store().updateFilters(createDefaultPhotoFilters());
  },

  hasActiveFilters: () => {
    const { filters } = store().params;
    return Boolean(
      filters.states?.length ||
        filters.category ||
        filters.type ||
        filters.themes?.length ||
        filters.tags?.length ||
        filters.search?.trim() ||
        filters.photographerId ||
        filters.uploadedFrom ||
        filters.uploadedTo,
    );
  },

  moderatePhoto: async (id, action) => {
    try {
      const updated = await getPhotoVogueService().moderatePhoto(id, {
        state: MODERATION_ACTION_TO_STATE[action],
      });
      store().setPhoto(updated);
      await store().fetchPhotos(store().params, true);
    } catch (error) {
      showErrorToast({ title: 'Unable to update photo' });
      set((state) => ({ ...state, error: (error as Error).message }));
    }
  },

  moderatePhotos: async (ids, action) => {
    try {
      const patches = buildPhotoBatchPatches(store().data, ids, action);
      const result = await getPhotoVogueService().patchPhotos(patches);
      if (result.failed.length) {
        showErrorToast({
          title: `${result.failed.length} photo(s) could not be updated`,
        });
      }
      await store().fetchPhotos(store().params, true);
    } catch (error) {
      showErrorToast({ title: 'Unable to update photos' });
      set((state) => ({ ...state, error: (error as Error).message }));
    }
  },

  updatePhoto: async (id, patch) => {
    try {
      const updated = await getPhotoVogueService().updatePhoto(id, patch);
      store().setPhoto(updated);
      await store().fetchPhotos(store().params, true);
      return updated;
    } catch (error) {
      showErrorToast({ title: 'Unable to update photo' });
      set((state) => ({ ...state, error: (error as Error).message }));
      return null;
    }
  },

  setPhoto: (photo) => {
    set((state) => ({
      ...state,
      data: state.data.map((item) => (item.id === photo.id ? photo : item)),
    }));
  },

  reset: () => {
    ++requestVersion;
    set((state) => ({
      ...state,
      ...initialState,
      params: defaultParams(),
    }));
  },
}));
